import React, { useState, useEffect } from "react";
import { func, string, object, number, bool } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import ReCAPTCHA from "react-google-recaptcha";

import TextField from "../../atoms/TextField";
import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import PopupOverlay from "../../molecules/PopupOverlay";
import SocialLogin from "../../molecules/SocialLogin";
import Icons from "../../icons";

import { register, sendVerificationLink } from "@quintype/bridgekeeper-js";
import { setLoading } from "../../helper/actions";
import { isValidEmail, validatePassword, getContinueToText } from "../../utils/utils";

import "./signup.m.css";

const SignUp = ({
  setLoading,
  googleClientId,
  facebookId,
  currentPath,
  sketchesHost,
  publisherAttributes,
  clientId,
  authHost,
  ssoLoginIsEnable,
  disableRecaptcha,
}) => {
  const [error, setError] = useState(null);
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPopUp, setShowPopUp] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);
  const targetText = getContinueToText(callbackUrl);

  useEffect(() => {
    localStorage.setItem("verify-redirect", redirectUrl);
  }, []);

  const setData = (e) => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setUser(userObj);
  };

  const onSubmitClick = async (e) => {
    e.preventDefault();

    if (disableRecaptcha && !recaptchaValue) {
      setError("Please verify that you are not a robot");
      return;
    }

    if (validate()) {
      // true -> field errors, false - no fields errors
      return;
    }

    const userObj = {
      name: user.name,
      email: user.email,
      username: user.email,
      password: user.password,
      "dont-login": false,
      metadata: {},
    };

    setLoading(true);

    try {
      const { user, message } = await register(userObj);
      if (!user && message === "User Already exists") {
        setError("Your email is already registered. Please login or register using an alternate email address");
        setLoading(false);
        return;
      }
      sendVerificationLink(user.email, callbackUrl);
      setShowPopUp(true);
      setLoading(false);
    } catch (err) {
      if (err.status === 409) {
        setError(`The email '${user.email}' already exists`);
      } else {
        setError("Oops! Something went wrong");
      }
    }
  };

  const validate = () => {
    const updatedUser = { ...user };
    let flag = false;

    if (!updatedUser.name) {
      updatedUser.nameError = "Full Name is required";
      flag = true;
    } else {
      updatedUser.nameError = "";
    }

    if (!updatedUser.email) {
      updatedUser.emailError = "Email address is required";
      flag = true;
    } else if (!isValidEmail(updatedUser.email)) {
      updatedUser.emailError = "Please enter a valid email address";
      flag = true;
    } else {
      updatedUser.emailError = "";
    }

    let pwdError = validatePassword(updatedUser.password);

    if (pwdError) {
      updatedUser.passwordError = pwdError;
      flag = true;
    } else {
      updatedUser.passwordError = "";
    }

    pwdError = validatePassword(updatedUser.confirmPassword, "confirm-password");

    if (pwdError) {
      updatedUser.confirmPasswordError = pwdError;
      flag = true;
    } else {
      updatedUser.confirmPasswordError = "";
    }

    if (updatedUser.password && updatedUser.confirmPassword) {
      // If password's exists then check if both are same
      if (updatedUser.password !== updatedUser.confirmPassword) {
        setError("Password do not match");
        flag = true;
      }
    }

    // If flag is true, there are field error's
    if (flag) {
      setUser(updatedUser); // update state with field errors
    }

    return flag;
  };

  const recaptchaHandler = (value) => {
    setRecaptchaValue(value);
  };

  useEffect(() => {
    showPopUp
      ? (global.document.body.style.overflow = "hidden")
      : (global.document.body.style.cssText = "overflow-x: hidden, overflow-y: auto");
  }, [showPopUp]);

  return (
    <>
      <form onSubmit={onSubmitClick}>
        <div styleName="container">
          <div styleName="wrapper">
            <a aria-label="Back Icon" styleName="back-button" href={callbackUrl}>
              <Icons height="14px" width="14px" type="back-icon" alt="Back Icon" />
              <span styleName="back-text">Back</span>
            </a>
            <div styleName="wrapper-content">
              <Icons type="palo-bangla" alt="Prothom Alo" className="palo-logo" />
              <h3 styleName="signup-text">Create an account </h3>
              <div styleName="continue-to">
                <span styleName="continue-to-text">to continue to</span>
                <span styleName="target-site">{targetText}</span>
              </div>

              <div styleName="content">
                <div styleName="left-content">
                  <SocialLogin
                    redirectUrl={redirectUrl}
                    callbackUrl={callbackUrl}
                    clientId={clientId}
                    ssoLoginIsEnable={ssoLoginIsEnable}
                    authHost={authHost}
                    facebookId={facebookId}
                    googleClientId={googleClientId}
                  />

                  <span styleName="or-text">Or</span>
                  <TextField
                    value={user.name}
                    error={user.nameError}
                    placeholder="Full name"
                    name="name"
                    inputType="type2"
                    onChange={setData}
                  />
                  <TextField
                    value={user.email}
                    error={user.emailError}
                    placeholder="Email"
                    name="email"
                    inputType="type2"
                    onChange={setData}
                  />
                  <PasswordField
                    value={user.password}
                    error={user.passwordError}
                    placeholder="Password"
                    name="password"
                    onChange={setData}
                  />
                  <PasswordField
                    value={user.confirmPassword}
                    error={user.confirmPasswordError}
                    placeholder="Confirm Password"
                    name="confirmPassword"
                    onChange={setData}
                  />

                  <span styleName="password-message">Use atleast 8 charcaters, mix of numbers & letters</span>
                  {disableRecaptcha && (
                    <ReCAPTCHA
                      style={{
                        "margin-bottom": "20px",
                        transform: !global.isMobile ? "scale(0.99)" : "scale(0.92)",
                        "-webkit-transform": !global.isMobile ? "scale(0.99)" : "scale(0.92)",
                      }}
                      sitekey={publisherAttributes.recaptchaSiteKey}
                      onChange={recaptchaHandler}
                    />
                  )}

                  <MessageView type="error" text={error} />

                  <Button content="REGISTER" type="submit" buttonType="type3" />

                  <span styleName="old-user">
                    Already a member?&nbsp;
                    <a
                      styleName="login"
                      href={`api/auth/v1/oauth/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUrl}&callback_uri=${callbackUrl}`}
                    >
                      Login
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div styleName="terms-privacy">
            By proceeding, you agree to{" "}
            <a aria-label="Terms of Use" styleName="terms-privacy-link" href={`${sketchesHost}/terms`}>
              Terms of Use
            </a>{" "}
            and{" "}
            <a aria-label="Privacy Policy" styleName="terms-privacy-link" href={`${sketchesHost}/privacy-policy`}>
              Privacy Policy
            </a>{" "}
            of Prothom Alo
          </div>
        </div>
      </form>
      {showPopUp && (
        <PopupOverlay
          lang={publisherAttributes.lang}
          popupMessage={`We have sent an activation email to you at ${user.email}. Please check your email box.`}
          buttonType="signup"
          buttonContent="Okay"
          onCloseHandler={() => setShowPopUp(false)}
        />
      )}
    </>
  );
};

SignUp.propTypes = {
  setLoading: func,
  googleClientId: string,
  facebookId: string,
  currentPath: string,
  sketchesHost: string,
  publisherAttributes: object,
  clientId: number,
  authHost: string,
  ssoLoginIsEnable: string,
  disableRecaptcha: bool,
};

const mapStateToProps = (state) => ({
  googleClientId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "google"], ""),
  facebookId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "fb"], ""),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  clientId: get(state, ["qt", "config", "publisher-attributes", "sso_login", "client_id"], 0),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  ssoLoginIsEnable: get(state, ["qt", "config", "publisher-attributes", "sso_login", "is_enable"], true),
  disableRecaptcha: get(state, ["qt", "config", "publisher-attributes", "disable_recaptcha"], true),
});

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
